import {ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {SiteDto, SiteService} from '../../../_services/configuration-services';
import {Subscription} from 'rxjs';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {TranslateUtils} from '../../translate-utils';
import {SiteUtils} from '../../site-utils';

/**
 * Toggle list of the available sites, bound to a form control holding the assigned sites.
 */
@Component({
    selector: 'app-site-assignment',
    templateUrl: './site-assignment.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SiteAssignmentComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SiteAssignmentComponent implements OnInit, OnDestroy, ControlValueAccessor {

    @Input() labelKey = 'Sites';

    sites: SiteDto[] = [];
    selectedSiteIds = new Set<string>();
    loaded = false;
    isDisabled = false;
    crtLang = TranslateUtils.defaultLanguage;

    private onChange: (value: SiteDto[]) => void = () => {};
    private onTouched: () => void = () => {};
    private langChangeSubscription: Subscription;

    constructor(
        private readonly siteService: SiteService,
        private readonly translateService: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.getCurrentLang();
        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.crtLang = event.lang;
            this.sortSites();
        });

        this.siteService.getSites().subscribe(sites => {
            this.sites = sites;
            this.sortSites();
            this.loaded = true;
        });
    }

    ngOnDestroy(): void {
        this.langChangeSubscription?.unsubscribe();
    }

    // ControlValueAccessor

    writeValue(value: SiteDto[] | null): void {
        this.selectedSiteIds = new Set((value ?? []).map(site => site.id));
    }

    registerOnChange(fn: (value: SiteDto[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    // Event handlers

    onSiteToggle(siteId: string, isAssigned: boolean): void {
        if (isAssigned) {
            this.selectedSiteIds.add(siteId);
        } else {
            this.selectedSiteIds.delete(siteId);
        }

        this.onChange(this.sites.filter(site => this.selectedSiteIds.has(site.id)));
        this.onTouched();
    }

    siteName(site: SiteDto): string {
        return SiteUtils.siteName(site, this.crtLang);
    }

    private sortSites(): void {
        this.sites = SiteUtils.sortByName(this.sites, this.crtLang);
    }
}
